import BaseForm from 'containers/Base/BaseForm.js';
import React, { useCallback } from 'react';
import { getInitialValues, validationSchema } from './options';
import staticText from 'static/text_display.json';
import { MessageInfo } from 'utils/constants/informationText';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { messageActions, messageSelectors } from 'containers/Pages/Messages';
import API from 'services/api';
import { transformRaw } from 'utils/DataTransform/Personnel/PersonnelList';
import { MessagePlaceholder } from 'utils/constants/message';
import { getUserAssignedDepartmentList, getUserRoles } from 'reducers/users';
import { COMPANY_ROLES, ROLES } from 'utils/constants/roles';
import { AccountHolderAndAbove, userHasRole } from 'utils/helper/roles';
export const initialState = {
  body: '',
  recipients: [],
  title: '',
};

export const generalSection = ({
  userIsAccountHolder,
  userAssignedDepartments,
}) => {
  return [
    {
      field: 'recipients',
      label: 'Recipients',
      required: true,
      size: 'half',
      placeholder: 'Select recipients',
      type: 'async-select',
      multiple: true,
      fetchOptions: API.fetchUserList,
      dataTransform: ({ data }) => {
        return transformRaw(data);
      },
      fetchFilters: {
        departments: userAssignedDepartments,
        roles: COMPANY_ROLES.filter(id => {
          if (id !== ROLES.SECURITY_COMPANY_MANAGER.id || userIsAccountHolder) {
            return true;
          }
          return false;
        }),
        archived: false,
      },
      optionLabelKey: 'label',
    },
    {
      field: 'title',
      label: 'Title',
      type: 'text',
      size: 'full',
      required: false,
    },
    {
      field: 'body',
      label: 'Message',
      type: 'textArea',
      maxLength: 250,
      required: true,
      placeholder: MessagePlaceholder.body,
    },
  ];
};
/**
 * Takes a title and trims/ adds the substring Re: or Fw: based on the message.
 * If
 * @param {string} title the title to alter based on if it is a reply or forward.
 * @param {boolean} reply if the current message is a reply
 * @return {string} the title that should be used.
 */
const setTitle = (title, reply) => {
  let result = '';
  if (reply) {
    if (!title || title.length === 0) {
      return 'Re: ';
    }
    if (title.toUpperCase().startsWith('RE:')) {
      result = title;
    } else if (title.toUpperCase().startsWith('FW: RE:')) {
      result = `Re: Fw: ${title.substring(8, title.length)}`;
    } else {
      result = `Re: ${title}`;
    }
  } else if (!title || title.length === 0) {
    return 'Fw: ';
  } else if (title.toUpperCase().startsWith('FW:')) {
    result = title;
  } else if (title.toUpperCase().startsWith('RE: FW:')) {
    result = `Fw: Re: ${title.substring(8, title.length)}`;
  } else {
    result = `Fw: ${title}`;
  }
  if (title.length > 54) {
    return result.substring(0, 54);
  }
  return result;
};
const MessageForm = ({
  isReply,
  isFwd,
  editMode,
  closeForm,
  setReplyMsg,
  setFwdMsg,
  setEditMode,
  persistentStateName,
}) => {
  //const users = useSelector(state => getGlobalUsersRecord(state));
  const details = useSelector(state =>
    messageSelectors.getMessageDetailsRaw(state),
  );
  const userAssignedDepartments = useSelector(state =>
    getUserAssignedDepartmentList(state),
  );
  const userRoles = useSelector(state => getUserRoles(state));
  const onCreate = vals => {
    dispatchCreateMessage({ message: vals });
  };
  const dispatch = useDispatch();
  const dispatchCreateMessage = useCallback(
    message => dispatch(messageActions.createRowEntry(message)),
    [dispatch],
  );
  const handleChange = () => {
    // setRecipients(vals.recipientIds);
  };
  const initialValues = editMode
    ? isReply
      ? getInitialValues({
          recipientId: details.senderId,
          recipientName: details.senderFullName,
          title: setTitle(details.title, isReply),
        })
      : isFwd
      ? getInitialValues({
          title: setTitle(details.title, isReply),
          body: details.body,
        })
      : initialState
    : initialState;

  const getTitle = (isReply, isFwd, editMode) => {
    if (editMode) {
      if (isReply) {
        return staticText.operational_view.messaging.reply;
      }
      if (isFwd) {
        return staticText.operational_view.messaging.forward;
      }
    } // if not edit mode then return static text
    return staticText.operational_view.messaging.create;
  };

  const resetFormValues = () => {
    closeForm();
    setReplyMsg(false);
    setFwdMsg(false);
    setEditMode(false);
  };

  return (
    <BaseForm
      useSendIcon={true}
      imageRequired={false}
      initialValues={initialValues}
      onChange={handleChange}
      formTitle={getTitle(isReply, isFwd, editMode)}
      closeForm={resetFormValues}
      saveHoverText={getTitle(isReply, isFwd, editMode)}
      onSubmit={onCreate}
      validationSchema={validationSchema}
      generalSection={generalSection({
        userIsAccountHolder: userHasRole({
          userRoles,
          requiredRoles: AccountHolderAndAbove,
        }),
        userAssignedDepartments: userAssignedDepartments,
      })}
      note={MessageInfo.note}
      persistentStateName={persistentStateName}
    />
  );
};

MessageForm.propTypes = {
  creator: PropTypes.object,
  createMessage: PropTypes.func,
  userMap: PropTypes.array,
  editMode: PropTypes.bool,
  details: PropTypes.object,
  closeForm: PropTypes.func,
  currentPage: PropTypes.string,
  persistentStateName: PropTypes.string,
  availableRoles: PropTypes.array,
  fromMessaging: PropTypes.bool,
  isReply: PropTypes.bool,
  isFwd: PropTypes.bool,
  setReplyMsg: PropTypes.func,
  setFwdMsg: PropTypes.func,
  setEditMode: PropTypes.func,
  userAssignedDepartments: PropTypes.array,
};
export default MessageForm;
